// The `evidence/guidance` READ — carved from `src/hooks/useHealthPathwaysStatus.ts`
// (now a bodied delegate).
//
// THE CROSS-LANE DEPENDENCY THIS FAMILY WAS DEFERRED ON IS SEVERED BY MAKING IT
// AN INPUT, and that is the whole content of the move. The pre-move hook called
// `useQueryEvidenceIntegrations()` — an INTEGRATIONS-lane read of
// `/evidence/integrations`, on the integrations lane's own cache identity — and
// the census recorded the consequence as a blocking ruling: "its status hook
// would move into a headless zone and import an app-side redux read".
//
// Two escapes were named there; neither is what shipped. Claiming the
// integrations hook would take a hook another lane owns and route an
// evidence-routed, integrations-consumed endpoint by consumer. Moving the
// derivation app-side would leave the family with no data surface at all.
// THE THIRD OPTION IS THE ONE THE DEPENDENCY ACTUALLY ADMITS: the fetch is
// integrations', the DERIVATION is evidence's, and the two are separable because
// nothing in the derivation needs the query — it reads four fields off one
// entry in a list. So the list crosses as an explicit input and the delegate
// supplies it, which is the `enabled`-gate severance this package already
// shipped for six reads, applied to a payload instead of a boolean.
//
// WHAT THAT BUYS BEYOND THE MOVE: the derivation was previously only reachable
// through a mocked query hook, so every assertion about it ran against a double
// of somebody else's read. As a function of its input it is directly testable,
// including the three malformed-payload branches nothing exercised before.
//
// NOTE FOR THE NEXT READER, because the census still says otherwise: the
// blocker's stated mechanism — that `useQueryEvidenceIntegrations` "imports
// `@/store` + `react-redux`" — is STALE. That hook moved onto `useAuthToken`
// before this carve. The dependency was real; the redux half of the reason had
// already gone.
import type { components } from '@heidi/schema/ml-scribe';
import { IntegrationInfoKey } from '@heidi/schema/ml-scribe';
import { useMemo } from 'react';

import type { HealthPathwaysSetupConfig } from '../../contracts';

type IntegrationsListResponse = components['schemas']['IntegrationsListResponse'];

export interface HealthPathwaysStatus {
  isAvailable: boolean;
  isSetUp: boolean;
  setupConfig: HealthPathwaysSetupConfig | null;
  /** Whether the integration requires user-driven setup */
  requiresSetup: boolean;
}

const parseSetupConfig = (raw: unknown): HealthPathwaysSetupConfig | null => {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  if (
    typeof r.zone_id !== 'string' ||
    typeof r.country_code !== 'string' ||
    typeof r.postcode !== 'string' ||
    (r.zone_type !== 'community' && r.zone_type !== 'hospital')
  ) {
    return null;
  }
  return {
    zone_id: r.zone_id,
    country_code: r.country_code,
    postcode: r.postcode,
    zone_type: r.zone_type,
  };
};

/**
 * Single source of truth for HealthPathways status.
 *
 * Reads `requires_setup` and `setup_config` off the HealthPathways entry of the
 * integrations list. No user-object dependency — the backend stores zone config
 * on the integration entry.
 *
 * @param integrations The integrations list payload, supplied by the caller —
 * see the header for why the read itself stays with the lane that owns it.
 */
export function useHealthPathwaysStatus(
  integrations: IntegrationsListResponse | undefined
): HealthPathwaysStatus {
  const hpIntegration = useMemo(() => {
    if (!integrations?.integrations) return null;
    return (
      integrations.integrations.find(
        (i) => i.key === IntegrationInfoKey.healthpathways
      ) ?? null
    );
  }, [integrations]);

  const isAvailable = !!hpIntegration?.is_available;
  const setupConfig = useMemo(
    () => parseSetupConfig(hpIntegration?.setup_config),
    [hpIntegration?.setup_config]
  );
  const isSetUp = setupConfig !== null;

  return {
    isAvailable,
    isSetUp,
    setupConfig,
    requiresSetup: hpIntegration?.requires_setup ?? false,
  };
}
